<template>
  <div class="w-full">
    <portal to="search">
      <div class="flex items-center sm:px-2 w-full divide-x divide-gray-200 gap-x-3">
        <h1 class="font-bold text-lg">
          Handover List
        </h1>
        <QuickSearchOptions :filters="filterForm" @apply="applyFilter" />
      </div>
    </portal>
    <portal to="quickActions">
      <div class="sm:space-x-3 flex items-center">
        <NoteFilterModal
          v-model="filterForm"
          :locations="locations"
          :warehouses="warehouses"
          :teams="teams"
          :filters="filters"
          @apply="applyFilter"
          @reset="reset"
        >
          <template #trigger="{open}">
            <button @click="open" class="btn-primary py-2 h-8 px-3">
              <Icon name="funnel" class="h-4 w-4" />
            </button>
          </template>
        </NoteFilterModal>
        <div v-if="hasFilter">
          <button @click="reset" class="py-2 text-sm">
            <Icon name="xmark" class="h-6 w-6" />
          </button>
        </div>
      </div>
    </portal>

    <div class="flex items-center justify-between gap-x-2">
      <div class="flex items-center gap-x-3">
        <div class="grid grid-cols-1 w-full">
          <select-input
            v-model="filterForm.warehouse"
            class="h-10"
            @input="applyFilter"
          >
            <option :value="`all`">All</option>
            <option v-for="warehouse in warehouses" :value="warehouse.id">{{ warehouse.name }}</option>
          </select-input>
        </div>
        <StatusToggle
          :status="filterForm.list_type"
          :options="listTypes"
          @toggle="toggleListType"
        />
      </div>

      <DeliveryTypeToggle
        :type="filterForm.method"
        :options="typeOptions"
        @toggle="toggleType"
      />
    </div>

    <div class="md:mt-5">
      <div v-if="list.data && list.data.length === 0" class="text-center py-5 text-sm text-gray-400 italic">
        No items found for the selected criteria
      </div>
      <div v-else class="space-y-5">
        <div class="">
          <div class="flow-root">
            <div class="overflow-x-auto">
              <div class="inline-block min-w-full py-2 align-middle sm:p-1">
                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table class="min-w-full divide-y divide-gray-300">
                    <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Note #</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Order #</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Customer</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Total QTY</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Items Collected at</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Delivery Date</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Delivery Location</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Vessel/Address
                      </th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                    <template v-for="note in list.data">
                      <tr  :id="'note-'+note.id"  @click="toggleNote(note)" :key="'note-'+note.id" class="cursor-pointer group" :class="{'bg-blue-100': hasNoteExpended(note)}">
                        <td class="group-hover:bg-blue-100 whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          <InertiaLink
                            :href="route('customer.orders.fulfilment-details', note.order_id)"
                            v-if="canAdmin('access-dispatch-notes')"
                             class="text-blue-500 hover:underline px-3 py-2"
                          >
                            #{{ note.id }}
                          </InertiaLink>
                          <span v-else>#{{ note.id }}</span>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap text-sm text-gray-500 capitalize">
                          <a v-if="canAdmin('access-orders')" :href="route('customer.orders.show', note.order_id)"
                             target="_blank"
                             class="text-blue-500 hover:underline px-3 py-2"
                          >
                            {{ note.order_id }}
                          </a>
                          <span v-else>{{ note.order_id }}</span>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap text-sm text-gray-500 capitalize">
                         <template v-if="note.customer_account">
                            {{ note.customer_account.name }}
                            <div>{{ note.customer_account.tin_number }}</div>
                          </template>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          {{ note.total_qty || 0 }}
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          {{ note.type === 'collection' ? 'Collection by Customer' : 'To be Delivered' }}
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                          <template v-if="note.type === 'delivery' && note.goods_collected_for_delivery_at">
                            {{ note.goods_collected_for_delivery_at | localDateTime }}
                          </template>
                          <template v-else-if="note.type === 'collection'">
                            <template v-if="note.status === 'completed'">
                              {{ note.customer_goods_collected_at | localDateTime }}
                            </template>
                            <template v-else>
                              Pending Collection
                            </template>
                          </template>
                          <template v-else>
                            ---
                          </template>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                          {{ note.delivery_date | localDate }}
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          <template v-if="note.type === 'collection'">
                            N/A
                          </template>
                          <template v-else>
                            {{ note.delivery_location }}
                          </template>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          <template v-if="note.delivery_location === 'island'">
                            {{ note.vessel }}
                          </template>
                          <template v-else-if="note.delivery_address">
                            {{ note.delivery_address }}
                          </template>
                          <template v-else>N/A</template>
                        </td>
                        <td class="group-hover:bg-blue-100 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          <Badge
                            class="px-3 py-1.5 rounded-lg capitalize"
                            :variant="
                              note.status === 'completed'
                              ? 'green'
                              : note.status === 'in-progress'
                              ? 'blue'
                              : note.status === 'void'
                              ? 'red'
                              : 'gray'
                            "
                          >
                            {{ note.status }}
                          </Badge>
                        </td>
                      </tr>
                      <transition
                        enter-active-class="transition ease-out duration-100 delay-100"
                        enter-class="transform opacity-0"
                        enter-to-class="transform opacity-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-class="transform opacity-100"
                        leave-to-class="transform opacity-0"
                      >
                        <tr v-if="hasNoteExpended(note)">
                          <td colspan="10" class="bg-gray-200">
                            <HandoverList
                              :note="note"
                              :warehouses="warehouses"
                              :locations="locations"
                              :filters="filters"
                              handover-list
                              @other-warehouse-seleted="onOtherWarehouseSelect($event, note.id)"
                            />
                          </td>
                        </tr>
                      </transition>
                    </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <pagination :links="list.links"></pagination>
    </div>
  </div>
</template>

<script>
import Layout from "@/Shared/Admin/Layout.vue";
import {mapValues, pickBy, sumBy} from "lodash";
import DeliveryTypeToggle from "../../Dispatch/Partials/DeliveryTypeToggle.vue";
import QuickSearchOptions from "../../Dispatch/Partials/QuickSearchOptions.vue";
import Icon from "@/Shared/Icon.vue";
import NoteFilterModal from "../../Dispatch/Partials/NoteFilterModal.vue";
import StatusToggle from "../../Dispatch/Partials/StatusToggle.vue";
import Pagination from "@/Shared/Pagination.vue";
import Badge from "@/Shared/Badge.vue";
import HandoverList from "./HandoverList.vue";
import SelectInput from "../../../../Shared/SelectInput.vue";

export default {
  name: 'HandoverIndex',
  components: {
    SelectInput,
    HandoverList,
    Badge, Pagination, StatusToggle, NoteFilterModal, Icon, QuickSearchOptions, DeliveryTypeToggle},
  layout: Layout,
  metaInfo: {title: 'Handover List - Dispatch Notes'},
  props: {
    list: Object,
    teams: Array,
    filters: Object,
    warehouses: Array,
    locations: Object,
  },

  data() {
    return {
      listTypes: [
        {
          value: 'pending',
          name: 'Pending',
        },
        {
          value: 'completed',
          name: 'Completed',
        }
      ],
      typeOptions: [
        {
          value: 'collection',
          name: 'Customer',
        },
        {
          value: 'delivery',
          name: 'Delivery',
        }
      ],
      form: {
        items: [],
      },
      filterForm: {
        search: this.filters.search || null,
        team: this.filters.team || null,
        warehouse: this.filters.warehouse || null,
        delivery_date: this.filters.delivery_date || null,
        method: this.filters.method || 'collection',
        list_type: this.filters.list_type || 'pending',
        subView: null,
      },
      activeNote: null,
    }
  },

  computed: {
    hasFilter() {
      return Object.keys(this.filters).some(
        key => (key !== 'status' || this.filters['status'] !== 'pending')
          && this.filters[key] !== null
          && this.filters[key] !== ''
      );
    },
    hasNoteExpended() {
      return note => {

        if (this.activeNote && Object.keys(this.activeNote).length) {
          return this.activeNote.id === note.id
        }
        return false;
      }
    }
  },

  methods: {
    toggleNote(note) {
      const url = new URL(window.location)

      if (this.hasNoteExpended(note)) {
        this.activeNote = null
        url.searchParams.delete('subView');
      } else {
        this.activeNote = note
        url.searchParams.set('subView', note.id)
      }
      window.history.pushState({}, '', url)
    },

    onOtherWarehouseSelect(warehouseId, noteId) {
      this.filterForm.subView = noteId
      this.filterForm.warehouse = warehouseId
      this.applyFilter()
    },

    applyFilter() {
      const query = pickBy(this.filterForm)
      this.$inertia.get(this.route('dispatches.handover-list', Object.keys(query).length ? query : {remember: 'forget'}))
    },

    reset() {
      this.filterForm = mapValues(this.filterForm, () => null)
      this.applyFilter()
    },

    toggleListType(type) {
      this.filterForm.list_type = type;
      if (type === 'completed') {
        this.filterForm.warehouse = 'all'
      }
      this.applyFilter()
    },

    toggleType(type) {
      this.filterForm.method = type;
      this.applyFilter()
    },
    applySubView() {
      const params = new URLSearchParams(window.location.search);
      const noteId = params.get('subView')
      if (noteId) {
        const note = this.list.data.find(imt => imt.id === parseInt(noteId))
        if (note) {
          this.toggleNote(note)
        }
      }
    }
  },

  mounted() {
    this.applySubView()
  }
}
</script>
